import { z } from 'zod';

export interface UpdateGuideTargetsParams {
  updates: UpdateGuideTargetItemDto[];
  reasonForChange: string;
  updatedBy: string;
}

export interface GetGuideTargetsParams {
  yearMo: number;
  clientIds?: number[];
  guideIds?: string[];
  targetTypeKeys?: string[];
  serviceAreaKeys?: string[];
  sortBy?: string;
  sortDirection?: string;
}

export interface GetGuideTargetsResult {
  items: GuideTargetPivotedRowDto[];
  count: number;
  targetTypeKeys: string[];
  serviceAreaKeys: string[];
  totalQueued: number;
  totalsByTargetType: GuideTargetTotalByTypeDto[];
}

export interface GuideTargetRowDto {
  id: number;
  userId: string;
  clientId: number;
  yearMo: number;
  target: number;
  targetType: string;
  targetTypeKey: string;
  serviceArea: string | null;
  serviceAreaKey: string | null;
  queuedMembersCount?: number | null;
  createdAt: number;
  updatedAt: number | null;
  updatedBy: { id: string; firstName: string; lastName: string } | null;
}

export interface GuideTargetHistoryDto {
  id?: number;
  guideTargetId: number;
  oldTarget: number;
  newTarget: number;
  reasonForChange: string | null;
  changedAt: number;
  changedBy: string | null;
  changedByUser: { id: string; firstName: string; lastName: string } | null;
}

export interface GuideTenureAndQueuedMembersDto {
  id: number;
  guideId: string;
  teamManagerId: string | null;
  customer: string;
  clientId: number;
  reemaServiceArea: string;
  reemaServiceAreaKey: string;
}

/**
 * Pivoted row: one per guide per client per service area, with all target types
 * as key-value pairs (targetTypeKey -> target value).
 * Queued fields: exact match on service area when present; otherwise sum/weighted
 * avg across all tenure rows for that guide+client+yearMo.
 */
export interface GuideTargetPivotedRowDto {
  userId: string;
  clientId: number;
  yearMo: number;
  serviceArea: string | null;
  serviceAreaKey: string | null;
  queuedMembersCount: number | null;
  avgQueuedMemberMonthsInProgram: number | null;
  targets: Record<string, number | 'NA'>;
  guideTargetIdsByTargetTypeKey: Record<string, number>;
  monthsTenured: number | null;
  startDate: string | null;
  teamManagerId: string | null;
  hasTargetsUpdatedByUser: boolean;
}

export interface GuideTargetTotalByTypeDto {
  clientId: number | null;
  serviceAreaKey: string | null;
  targetTypeKey: string;
  total: number;
}

export interface GuideTargetsAvailablePeriodsDto {
  yearMoValues: number[];
}

export interface PaginatedGuideTargetsDto {
  // One row per guide per client per service area; target types in `targets`
  items: GuideTargetPivotedRowDto[];
  count: number;
  targetTypeKeys: string[];
  serviceAreaKeys: string[];
  totalQueued: number;
  totalsByTargetType: GuideTargetTotalByTypeDto[];
}

export const UpdateGuideTargetItemSchema = z
  .object({
    guideTargetId: z.number(),
    target: z.coerce.number().int().min(0),
  })
  .strict();

export type UpdateGuideTargetItemDto = z.infer<
  typeof UpdateGuideTargetItemSchema
>;

function plainTextFromHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/\u00a0/g, ' ')
    .trim();
}

/** True when the rich-text reason has visible content (empty Quill HTML is treated as empty). */
export function isGuideTargetReasonForChangeNonEmpty(html: string): boolean {
  return plainTextFromHtml(html).length > 0;
}

/** API / server: validates shape only; reason is enforced when updates actually change persisted targets. */
export const UpdateGuideTargetsRequestDtoSchema = z
  .object({
    updates: z.array(UpdateGuideTargetItemSchema),
    reasonForChange: z.string(),
  })
  .strict();

/**
 * Care portal form: require reason only when at least one target differs from `baselineTargets`
 * (same order as `updates`).
 */
export function createUpdateGuideTargetsFormSchema(
  baselineTargets: readonly number[]
) {
  return UpdateGuideTargetsRequestDtoSchema.superRefine((data, ctx) => {
    if (data.updates.length !== baselineTargets.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'updates must match baseline length',
        path: ['updates'],
      });
      return;
    }
    const hasTargetChange = data.updates.some(
      (u, i) => u.target !== baselineTargets[i]
    );
    if (!hasTargetChange) return;
    if (!isGuideTargetReasonForChangeNonEmpty(data.reasonForChange)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['reasonForChange'],
      });
    }
  });
}

export type UpdateGuideTargetsRequestDto = z.infer<
  typeof UpdateGuideTargetsRequestDtoSchema
>;
